import { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

type AttachmentType = "image" | "file" | "location" | "link";

interface AttachmentMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectImage: () => void;
  onSelectFile: () => void;
  onSelectLocation: () => void;
  onSelectLink: () => void;
}

export default function AttachmentMenu({
  isOpen,
  onClose,
  onSelectImage,
  onSelectFile,
  onSelectLocation,
  onSelectLink,
}: AttachmentMenuProps) {
  const { t } = useTranslation("dashboard");
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const items: { icon: string; labelKey: string; color: string; onClick: () => void }[] = [
    { icon: "ri-image-line", labelKey: "attach.image", color: "text-purple-500", onClick: onSelectImage },
    { icon: "ri-file-line", labelKey: "attach.document", color: "text-blue-500", onClick: onSelectFile },
    { icon: "ri-map-pin-line", labelKey: "attach.location", color: "text-green-500", onClick: onSelectLocation },
    { icon: "ri-link", labelKey: "attach.link", color: "text-orange-500", onClick: onSelectLink },
  ];

  return (
    <div
      ref={menuRef}
      className="absolute bottom-full left-0 mb-2 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-1.5 min-w-[180px] z-50 animate-in fade-in slide-in-from-bottom-2 duration-150"
    >
      {items.map((item) => (
        <button
          key={item.labelKey}
          onClick={() => {
            item.onClick();
            onClose();
          }}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap"
        >
          <span className={`w-8 h-8 flex items-center justify-center rounded-full bg-background-200/60 ${item.color}`}>
            <i className={`${item.icon} text-base`}></i>
          </span>
          {t(item.labelKey)}
        </button>
      ))}
    </div>
  );
}